/**
 * The document photo pipeline's logic (piece 9): the order of its steps, the
 * gross-orientation proposal read from the EXIF tag, the fine skew estimate by
 * projection profile, and the rule that grayscale and downsample wait for a
 * human confirmation. Detection proposes; it never applies.
 */
export const PIPELINE_STEPS = ['grossOrientation', 'fineDeskew', 'autoCrop', 'grayscale', 'downsample'] as const;
export type PipelineStep = (typeof PIPELINE_STEPS)[number];

/** The steps that run only after a person confirms or corrects rotation and crop. */
export const STEPS_AFTER_CONFIRM: readonly PipelineStep[] = ['grayscale', 'downsample'];

export type Rotation = 0 | 90 | 180 | 270;
export const ROTATIONS: readonly Rotation[] = [0, 90, 180, 270];

export function isRotation(value: unknown): value is Rotation {
  return (ROTATIONS as readonly unknown[]).includes(value);
}

/** The EXIF orientation tag as a proposed quarter turn; mirrored variants take their rotation and lose the mirror. */
export function exifRotation(orientation: number | undefined | null): Rotation {
  switch (orientation) {
    case 3:
    case 4:
      return 180;
    case 5:
    case 6:
      return 90;
    case 7:
    case 8:
      return 270;
    default:
      return 0;
  }
}

export type Corner = readonly [number, number];
export type CropCorners = readonly [Corner, Corner, Corner, Corner];

/** Edge detection found no confident document boundary: the error state with Retry and Adjust Manually. */
export function noConfidentBoundary(corners: CropCorners | null, width: number, height: number): boolean {
  if (corners === null) return true;
  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  const w = Math.max(...xs) - Math.min(...xs);
  const h = Math.max(...ys) - Math.min(...ys);
  // A boundary that is the whole frame, or a sliver, is not a document edge.
  if (w >= width * 0.98 && h >= height * 0.98) return true;
  return w < width * 0.2 || h < height * 0.2;
}

export function confirmBlocksPipeline(confirm: { confirmedRotation: number | null; confirmedCropCorners: unknown | null }): boolean {
  return confirm.confirmedRotation === null || confirm.confirmedCropCorners === null || confirm.confirmedCropCorners === undefined;
}

export interface SkewOptions {
  maxDegrees?: number;
  stepDegrees?: number;
  inkThreshold?: number;
  /** The relative gain over the unrotated profile required to trust an angle. */
  minGain?: number;
}

function rowVariance(gray: Uint8Array, width: number, height: number, degrees: number, inkThreshold: number): number {
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = width / 2;
  const cy = height / 2;
  const rows = new Float64Array(height);
  for (let y = 0; y < height; y++) {
    let ink = 0;
    for (let x = 0; x < width; x++) {
      // Sample the source pixel that lands here after rotating by -degrees.
      const dx = x - cx;
      const dy = y - cy;
      const sx = Math.round(cx + dx * cos + dy * sin);
      const sy = Math.round(cy - dx * sin + dy * cos);
      if (sx < 0 || sy < 0 || sx >= width || sy >= height) continue;
      if (gray[sy * width + sx]! < inkThreshold) ink++;
    }
    rows[y] = ink;
  }
  let mean = 0;
  for (let y = 0; y < height; y++) mean += rows[y]!;
  mean /= height;
  let variance = 0;
  for (let y = 0; y < height; y++) variance += (rows[y]! - mean) ** 2;
  return variance / height;
}

/**
 * Fine skew by projection profile: text lines make the row ink counts peaky
 * once the page is straight, so the angle whose profile has the highest
 * variance is the skew. Returns the correction in degrees, 0 when no angle
 * beats the unrotated profile by the gain, so an uncertain guess proposes nothing.
 */
export function estimateSkewAngle(gray: Uint8Array, width: number, height: number, options: SkewOptions = {}): number {
  const { maxDegrees = 5, stepDegrees = 0.5, inkThreshold = 128, minGain = 0.05 } = options;
  if (gray.length !== width * height || width < 8 || height < 8) return 0;
  const base = rowVariance(gray, width, height, 0, inkThreshold);
  let best = 0;
  let bestVariance = base;
  for (let a = -maxDegrees; a <= maxDegrees + 1e-9; a += stepDegrees) {
    const angle = Math.round(a * 1000) / 1000;
    if (angle === 0) continue;
    const v = rowVariance(gray, width, height, angle, inkThreshold);
    if (v > bestVariance) {
      bestVariance = v;
      best = angle;
    }
  }
  return bestVariance >= base * (1 + minGain) ? best : 0;
}

export const TARGET_DPI_FLOOR = 200;

/** The downsample stops at the floor: a photo under it keeps its size, one over it scales to exactly the floor. */
export function downsampleTarget(widthPx: number, heightPx: number, sourceDpi: number, targetDpi = TARGET_DPI_FLOOR): { width: number; height: number; scale: number } {
  if (sourceDpi <= targetDpi || sourceDpi <= 0) return { width: widthPx, height: heightPx, scale: 1 };
  const scale = targetDpi / sourceDpi;
  return { width: Math.max(1, Math.round(widthPx * scale)), height: Math.max(1, Math.round(heightPx * scale)), scale };
}

/** Whether the pipeline applies to an upload: the type is listed on a DocumentPhotoProcessing row. */
export function pipelineApplies(fileTypeId: string, configurations: readonly { applicableFileTypes: readonly string[] }[]): boolean {
  return configurations.some((c) => c.applicableFileTypes.includes(fileTypeId));
}
